import DeleteIcon from '@mui/icons-material/Delete'
import RestoreIcon from '@mui/icons-material/Restore'
import { Box, Menu, MenuItem } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import {
  useDeleteCaseMutation,
  useRestoreImageMutation,
} from '@/store/apiService'
import { removeTrashSelectedRow } from '@/store/project'

interface ActionMenuProps {
  anchorEl: null | HTMLElement
  handleMenuClose: () => void
  caseData: any
  refetch: () => void
}

const TrashActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  handleMenuClose,
  caseData,
  refetch,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'TrashProject',
  })

  const [isDeleteDialog, setIsDeleteDialog] = useState(false)
  const [caseIdToDelete, setCaseIdToDelete] = useState<string | null>(null)
  const caseId = caseData?.id
  const dispatch = useDispatch()

  const [deleteCase] = useDeleteCaseMutation()
  const [restoreImage] = useRestoreImageMutation()

  const projectRestore = async () => {
    handleMenuClose()

    if (caseId) {
      try {
        await restoreImage({
          ids: [caseId],
        }).unwrap()
        dispatch(removeTrashSelectedRow(caseId))
        toast.success(t('TrashActions.projectRestored'))
        refetch()
      } catch (err) {
        toast.error(t('TrashActions.projectRestoreFailed'))
      }
    }
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
              color: 'white',
              mt: 1,
              boxShadow:
                '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
              borderRadius: '4px',
            },
          },
          list: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={projectRestore}
          sx={{ '&:hover': { backgroundColor: '#33393E' } }}
        >
          <RestoreIcon sx={{ mr: '12px' }} />
          {t('TrashActions.restore')}
        </MenuItem>

        <MenuItem
          onClick={async () => {
            handleMenuClose()
            setCaseIdToDelete(caseId)
            setIsDeleteDialog(true)
          }}
          sx={{ '&:hover': { backgroundColor: '#33393E' } }}
        >
          <DeleteIcon sx={{ mr: '12px' }} />
          {t('TrashActions.delete')}
        </MenuItem>
      </Menu>
      <Dialog
        triggerElement={null}
        isOpen={isDeleteDialog}
        onClose={() => {
          setIsDeleteDialog(false)
          setCaseIdToDelete(null)
        }}
        title={t('TrashActionsDialog.title')}
        content={<p>{t('TrashActionsDialog.description')}</p>}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label="CANCEL"
              onClick={() => {
                setIsDeleteDialog(false)
                handleMenuClose()
                setCaseIdToDelete(null)
              }}
            />
            <Button
              variant="contained"
              label="DELETE"
              onClick={async () => {
                if (caseIdToDelete) {
                  try {
                    await deleteCase({
                      ids: [caseIdToDelete],
                      hard_delete: true,
                    }).unwrap()
                    setIsDeleteDialog(false)
                    dispatch(removeTrashSelectedRow(caseIdToDelete))
                    toast.success(t('TrashActionsDialog.deleteSuccess'))
                    refetch()
                  } catch (err) {
                    toast.error(t('TrashActionsDialog.deleteError'))
                  }
                }
              }}
            />
          </Box>
        }
      />
    </>
  )
}

export default TrashActionMenu
